import React, { useMemo, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { Info } from "lucide-react";

const usd = (n, dec = 0) =>
  isFinite(n) ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec })}` : "—";
const pct = (n, dec = 1) => (isFinite(n) ? `${n.toFixed(dec)}%` : "—");

function BigKPI({ label, value, sub, color, testid }) {
  return (
    <div className="glass p-5 fade-up" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-mono text-ink-3 mb-1.5">{label}</div>
      <div className="font-display text-3xl md:text-4xl font-bold tracking-tight tabular-nums" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-ink-3 mt-1">{sub}</div>}
    </div>
  );
}

/** Daily compounding + CA appreciation — reads the shared inputs on the left. */
export default function CompoundView({ principal, dailyRate, days, caPrice, monthlyAppn }) {
  const sim = useMemo(() => {
    const r = (Number(dailyRate) || 0) / 100;
    const m = (Number(monthlyAppn) || 0) / 100;
    const P = Number(principal) || 0;
    const D = Math.max(1, Math.round(Number(days) || 0));
    const series = [];
    let bal = P;
    for (let d = 0; d <= D; d++) {
      if (d > 0) bal *= 1 + r;
      const priceMult = Math.pow(1 + m, d / 30);
      if (d % Math.max(1, Math.round(D / 60)) === 0 || d === D) {
        series.push({ day: d, balance: bal, simple: P * (1 + r * d), combined: bal * priceMult });
      }
    }
    const finalBal = P * Math.pow(1 + r, D);
    const priceMultFinal = Math.pow(1 + m, D / 30);
    const rows = [];
    for (let d = 30; d < D; d += 30) rows.push({ day: d, balance: P * Math.pow(1 + r, d), priceMult: Math.pow(1 + m, d / 30) });
    rows.push({ day: D, balance: finalBal, priceMult: priceMultFinal });
    return {
      series, finalBal, rows, priceMultFinal, D, P,
      interest: finalBal - P,
      retPct: P ? ((finalBal - P) / P) * 100 : 0,
      futurePrice: (Number(caPrice) || 0) * priceMultFinal,
      combined: finalBal * priceMultFinal,
      combinedPct: P ? ((finalBal * priceMultFinal - P) / P) * 100 : 0,
    };
  }, [principal, dailyRate, days, caPrice, monthlyAppn]);

  return (
    <div className="space-y-5" data-testid="compound-view">
      <div className="grid sm:grid-cols-3 gap-4">
        <BigKPI label={`Balance after ${sim.D} days`} value={usd(sim.finalBal)} sub={`from ${usd(sim.P)} at ${dailyRate}%/day compounded`} color="#4060FF" testid="quick-kpi-balance" />
        <BigKPI label="Interest earned" value={usd(sim.interest)} sub={`${pct(sim.retPct)} total return`} color="#00863A" testid="quick-kpi-interest" />
        <BigKPI label="With CA appreciation" value={usd(sim.combined)} sub={`${pct(sim.combinedPct)} combined return`} color="#7C3AED" testid="quick-kpi-combined" />
      </div>

      <div className="glass p-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="quick-ca-strip">
        <div className="text-sm text-ink-2">
          CA price: <span className="font-mono font-bold text-ink">{usd(caPrice, 2)}</span>
          <span className="mx-2 text-ink-3">→</span>
          <span className="font-mono font-bold" style={{ color: "#7C3AED" }}>{usd(sim.futurePrice, 2)}</span>
          <span className="ml-2 chip text-[11px]" style={{ background: "rgba(124,58,237,0.10)", color: "#7C3AED", fontWeight: 700 }}>
            ×{sim.priceMultFinal.toFixed(2)}
          </span>
        </div>
        <div className="text-[11px] text-ink-3 font-mono">at {Number(monthlyAppn) || 0}%/month over {sim.D} days</div>
      </div>

      <div className="glass p-5" data-testid="quick-chart">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-3 mb-3">Growth curve</div>
        <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <AreaChart data={sim.series} margin={{ left: 8, right: 8, top: 4 }}>
            <defs>
              <linearGradient id="qcBal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4060FF" stopOpacity={0.35} /><stop offset="100%" stopColor="#4060FF" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="qcComb" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.3} /><stop offset="100%" stopColor="#7C3AED" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,18,38,0.06)" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} tickFormatter={(d) => `d${d}`} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} width={54} />
            <RTooltip formatter={(v, name) => [usd(v), name]} labelFormatter={(d) => `Day ${d}`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="simple" name="No reinvest" stroke="#94A3B8" strokeDasharray="5 4" fill="none" strokeWidth={1.5} />
            <Area type="monotone" dataKey="balance" name="Compounded daily" stroke="#4060FF" fill="url(#qcBal)" strokeWidth={2.5} />
            <Area type="monotone" dataKey="combined" name="+ CA appreciation" stroke="#7C3AED" fill="url(#qcComb)" strokeWidth={2.5} />
          </AreaChart>
        </ResponsiveContainer>
        </div>
      </div>

      <div className="glass p-5 overflow-x-auto" data-testid="quick-breakdown">
        <div className="text-xs font-mono uppercase tracking-wider text-ink-3 mb-3">Monthly breakdown</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-ink-3">
              <th className="py-1.5 pr-4">Day</th>
              <th className="py-1.5 pr-4">Balance</th>
              <th className="py-1.5 pr-4">Interest to date</th>
              <th className="py-1.5 pr-4">CA price</th>
              <th className="py-1.5">With appreciation</th>
            </tr>
          </thead>
          <tbody>
            {sim.rows.map((r) => (
              <tr key={r.day} className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }}>
                <td className="py-2 pr-4 font-mono text-xs text-ink-3">Day {r.day}</td>
                <td className="py-2 pr-4 font-mono font-semibold tabular-nums">{usd(r.balance)}</td>
                <td className="py-2 pr-4 font-mono tabular-nums" style={{ color: "#00863A" }}>{usd(r.balance - sim.P)}</td>
                <td className="py-2 pr-4 font-mono tabular-nums text-ink-2">{usd((Number(caPrice) || 0) * r.priceMult, 2)}</td>
                <td className="py-2 font-mono font-semibold tabular-nums" style={{ color: "#7C3AED" }}>{usd(r.balance * r.priceMult)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="glass p-4 flex items-start gap-3" data-testid="quick-disclaimer">
        <Info size={15} className="text-[#4060FF] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-ink-2 leading-relaxed">
          Every input here is an <em>assumption</em> — daily rates vary and CA price can go up <em>or down</em>.
          This is a what-if projection for planning and presentations, not a guarantee and not financial advice.
        </p>
      </div>
    </div>
  );
}
